import {inject, observer} from 'mobx-react';
import React, {useEffect, useState} from 'react';
import CourseAnalyticsLearnersUI from '../view/CourseAnalyticsLearnersUI';
import CourseAnalyticsLearnersStore from '../store/CourseAnalyticsLearnersStore';
import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import {
  CourseAnalyticsLearnerDetails,
  CourseLearnersAnalyticsItem,
} from '../../../../../data/models/Gradebook/CourseAnalyticsModels';
import LearningJourneyStore from '../../../../../modules/LearningJourney/store/LearningJourneyStore';
import {LearningJourney} from '../../../../../modules/LearningJourney';
import {downloadCSV} from '../../../../../utils/downloadCsv';
import {analyticsText} from '../../../analyticsText';
import {hasValue} from '../../../../../utils/typecheck';

interface Props {
  courseAnalyticsLearnersStore?: CourseAnalyticsLearnersStore;
  learningJourneyStore?: LearningJourneyStore;
}

const CourseAnalyticsLearnersContainer = inject(
  'courseAnalyticsLearnersStore',
  'learningJourneyStore',
)(
  observer(({courseAnalyticsLearnersStore, learningJourneyStore}: Props) => {
    const [courseData, setCourseData] = useState<CourseLearnersAnalyticsItem[]>(
      [],
    );
    const [search, setSearch] = useState('');
    const [selectedLearnerGradebook, setSelectedLearnerGradeboook] =
      useState<CourseAnalyticsLearnerDetails>();
    const {courseId, organizationId} = useParams();
    const [sortBy, setSortBy] = useState('Name');
    const [sortAsc, setSortAsc] = useState(false);
    const navigate = useNavigate();

    const [isGradable, courseName, passingScore] =
      useOutletContext<[boolean, string, number]>();

    useEffect(() => {
      fetchGradebook();
      return () => {
        setSelectedLearnerGradeboook(undefined);
      };
    }, [courseId, sortBy, sortAsc]);

    const fetchGradebook = () => {
      if (courseId && organizationId) {
        courseAnalyticsLearnersStore!
          .getCourseLearnersData(organizationId, courseId, sortBy, sortAsc)
          .then(setCourseData);
      }
    };

    const onBack = () => {
      navigate(`/manage/${organizationId}/analytics/`, {replace: true});
    };

    const onSelectLearner = (learnerId: string) => {
      if (organizationId && courseId) {
        courseAnalyticsLearnersStore!
          .getCourseLearnerAnalyticsDetails(organizationId, courseId, learnerId)
          .then(setSelectedLearnerGradeboook);
      }
    };

    const onCloseLearnerDetails = () => {
      setSelectedLearnerGradeboook(undefined);
    };

    const onOpenLearningJourney = (learnerId: string, lessonId: string) => {
      learningJourneyStore!.openJourney(learnerId, lessonId, courseId || '');
    };

    const onExport = () => {
      if (isGradable) {
        downloadCSV(
          [
            [
              analyticsText.learner,
              analyticsText.status,
              analyticsText.score,
              analyticsText.progress,
            ],
            ...courseData.map(learner => [
              learner.name,
              learner.status === 0
                ? analyticsText.completed
                : learner.status === 1
                ? analyticsText.inProgress
                : analyticsText.notStarted,
              hasValue(learner.avgScore) ? learner.avgScore!.toString() : '-',
              learner.progress.toString(),
            ]),
          ],
          `${courseName}_learners.csv`,
        );
      } else {
        downloadCSV(
          [
            [
              analyticsText.learner,
              analyticsText.status,
              analyticsText.duration,
              analyticsText.progress,
            ],
            ...courseData.map(learner => [
              learner.name,
              learner.status === 0
                ? analyticsText.completed
                : learner.status === 1
                ? analyticsText.inProgress
                : analyticsText.notStarted,
              '00:00:00',
              learner.progress.toString(),
            ]),
          ],
          `${courseName}_learners.csv`,
        );
      }
    };

    const onChangeSort = (field: string, asc: boolean) => {
      setSortAsc(asc);
      setSortBy(field);
    };

    const onChangeGrade = async (
      learnerId: string,
      lessonId: string,
      newGrade: number,
    ) => {
      if (organizationId && courseId) {
        return await courseAnalyticsLearnersStore!.changeLessonGrade(
          organizationId,
          courseId,
          learnerId,
          lessonId,
          newGrade,
        );
      }
      return false;
    };

    return (
      <>
        <CourseAnalyticsLearnersUI
          courseAnalyticsLearners={courseData}
          isGradable={isGradable}
          passingScore={passingScore}
          onBack={onBack}
          onSelectLearner={onSelectLearner}
          selectedLearnerGradebook={selectedLearnerGradebook}
          onCloseLearnerDetails={onCloseLearnerDetails}
          onOpenLearningJourney={onOpenLearningJourney}
          search={search}
          onExport={onExport}
          setSeach={setSearch}
          organizationId={organizationId || ''}
          courseId={courseId || ''}
          sortBy={sortBy}
          sortAsc={sortAsc}
          onChangeSort={onChangeSort}
          onChangeGrade={onChangeGrade}
        />
        <LearningJourney />
      </>
    );
  }),
);

export default CourseAnalyticsLearnersContainer;
